import { warnText } from '../ui';
import { CSSProperties, useEffect, useState } from 'react';
import { JiraListings, JiraProject } from './listings';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const TRUNCATED_HINT =
  'More projects match than are shown. Refine your search.';
const NO_MATCH_HINT = 'No visible software project matches.';
const SEARCH_HINT =
  'Type at least two characters of the project name or key. Only the software projects PANDA can poll are listed.';

const container: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  marginBottom: 14,
  maxWidth: 560,
  position: 'relative',
};

const labelStyle: CSSProperties = {
  fontSize: 12.5,
  fontWeight: 600,
  color: 'var(--bui-fg-secondary)',
};

const input = (invalid: boolean): CSSProperties => ({
  fontSize: 13,
  padding: '7px 10px',
  borderRadius: 6,
  border: `1px solid ${
    invalid ? 'var(--bui-fg-danger)' : 'var(--bui-border-2)'
  }`,
  background: 'var(--bui-bg-neutral-1)',
  color: 'var(--bui-fg-primary)',
  width: '100%',
});

const results: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  border: '1px solid var(--bui-border-1)',
  borderRadius: 6,
  background: 'var(--bui-bg-neutral-1)',
  maxHeight: 220,
  overflowY: 'auto',
};

const result: CSSProperties = {
  textAlign: 'left',
  fontSize: 13,
  padding: '6px 10px',
  border: 'none',
  background: 'transparent',
  color: 'var(--bui-fg-primary)',
  cursor: 'pointer',
};

const hint: CSSProperties = {
  fontSize: 11.5,
  color: 'var(--bui-fg-secondary)',
};
const errorText: CSSProperties = {
  fontSize: 12,
  color: 'var(--bui-fg-danger)',
};

type Search =
  | { kind: 'closed' }
  | { kind: 'searching' }
  | { kind: 'listed'; projects: JiraProject[]; isLast: boolean }
  | { kind: 'unavailable'; detail: string };

export const JiraProjectPicker = ({
  id,
  title,
  value,
  onChange,
  listings,
  error,
  unavailableReason,
  debounceMs = SEARCH_DEBOUNCE_MS,
}: {
  id: string;
  title: string;
  value: string;
  onChange: (projectKey: string) => void;
  listings: JiraListings;
  error?: string;
  unavailableReason?: string;
  debounceMs?: number;
}) => {
  const [typed, setTyped] = useState(value);
  const [hasTyped, setHasTyped] = useState(false);
  const [search, setSearch] = useState<Search>({ kind: 'closed' });

  useEffect(() => setTyped(value), [value]);

  useEffect(() => {
    if (!hasTyped || unavailableReason) return undefined;
    const query = typed.trim();
    if (query.length < MIN_QUERY_LENGTH) {
      setSearch({ kind: 'closed' });
      return undefined;
    }
    let current = true;
    const timer = setTimeout(() => {
      setSearch({ kind: 'searching' });
      listings.searchProjects(query).then(outcome => {
        if (!current) return;
        setSearch(
          outcome.status === 'listed'
            ? {
                kind: 'listed',
                projects: outcome.projects,
                isLast: outcome.isLast,
              }
            : { kind: 'unavailable', detail: outcome.detail },
        );
      });
    }, debounceMs);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [typed, hasTyped, unavailableReason, listings, debounceMs]);

  const onType = (raw: string) => {
    setTyped(raw);
    setHasTyped(true);
    onChange(raw);
  };

  const onPick = (project: JiraProject) => {
    setTyped(project.key);
    setSearch({ kind: 'closed' });
    onChange(project.key);
  };

  return (
    <div style={container}>
      <label htmlFor={id} style={labelStyle}>
        {title}
      </label>
      <input
        id={id}
        style={input(Boolean(error))}
        value={typed}
        aria-invalid={Boolean(error)}
        autoComplete="off"
        onChange={event => onType(event.target.value)}
      />

      {search.kind === 'searching' && <span style={hint}>Searching…</span>}

      {search.kind === 'listed' && (
        <>
          {search.projects.length === 0 ? (
            <span style={hint}>{NO_MATCH_HINT}</span>
          ) : (
            <div style={results}>
              {search.projects.map(project => (
                <button
                  key={project.key}
                  type="button"
                  style={result}
                  onClick={() => onPick(project)}
                >
                  {project.name} ({project.key})
                </button>
              ))}
            </div>
          )}
          {!search.isLast && <span style={warnText}>{TRUNCATED_HINT}</span>}
        </>
      )}

      {search.kind === 'unavailable' && (
        <span style={warnText}>
          Projects could not be listed ({search.detail}). Type the key itself.
        </span>
      )}

      {error ? (
        <span role="alert" style={errorText}>
          {error}
        </span>
      ) : (
        <span style={hint}>{unavailableReason ?? SEARCH_HINT}</span>
      )}
    </div>
  );
};
